import React, {
  type FunctionComponent,
  type PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from 'react'
import {
  Role,
  useAiAgentExecuteMutation,
  type MessageInput,
  type AgentRequestInput,
  type AgentResponse,
  type Message,
} from '../../../__codegen__/api'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { FeatureToggle } from '../../../access-control/featureToggles'
import { Permission } from '../../../access-control/permissions'
import { useDashMessages } from '../../../intl/intl.hooks'
import { useLocalForageKey } from '../../local-storage/useLocalForageKey.hooks'
import { trackEvent } from '../../events/trackEvent'
import { useCurrentAccountContext } from '../../current-account/CurrentAccountContext'
import { usePartnerId, useRetailerId } from '../../routing/params.hooks'
import { AiAgentContext } from './AiAgentContext'
import {
  type AiAgentContextValue,
  type RegisteredFunction,
  type RegisteredFunctionInput,
  type RegisteredData,
  type RegisteredPrompt,
} from './utils/types'
import { useAiAgentConversation } from './AiAgentConversation.hook'
import { CommentModalProvider } from './CommentModalContext'
import { useConversationHistory } from './utils/useConversationHistory.hooks'

// ---------------------------------------------------------------------------
// Lightweight stub (currently active)
// ---------------------------------------------------------------------------

const noop = () => {}
const noopAsync = async () => {}
const noopPromise = () => Promise.resolve({ messages: [], finalResult: 'disabled', summary: null })

const STUB_CONTEXT_VALUE: AiAgentContextValue = {
  registerAiFunction: noop,
  getRegisteredFunction: () => undefined,
  removeAiFunction: noop,
  registerAiData: noop,
  getRegisteredData: () => undefined,
  removeAiData: noop,
  registerAiPrompt: noop,
  getRegisteredPrompts: () => [],
  removeAiPrompt: noop,
  registerMaxIterations: noop,
  removeMaxIterations: noop,
  executeAgentRequest: noopPromise as AiAgentContextValue['executeAgentRequest'],
  isAgentAvailable: false,
  isAgentUserEnabled: false,
  isAgentPowered: false,
  isChatOpen: false,
  openChat: noop,
  closeChat: noop,
  isPageAiReady: false,
  setPageAiReady: noop,
  messages: [],
  sendMessage: noopAsync as AiAgentContextValue['sendMessage'],
  clearConversation: noop,
  clearConversationWithoutSaving: noop,
  updateMessage: noopAsync as AiAgentContextValue['updateMessage'],
  updateMessageFeedback: noopAsync as AiAgentContextValue['updateMessageFeedback'],
  updateMessageComment: noopAsync as AiAgentContextValue['updateMessageComment'],
  loading: false,
  setEnabled: noop,
  summary: null,
  conversationHistory: [],
  currentConversationId: null,
  loadConversation: noop,
  deleteConversation: noop,
  clearAllConversations: noop,
}

/**
 * Lightweight stub provider for AiAgent.
 *
 * Only enable the AI agent if the user has access to the feature.
 */
export const AiAgentProvider: FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {
  const hasAccess = useAccessControl()
  const hasSystemAccess = hasAccess({
    featureToggles: FeatureToggle.IppAiAgent,
    permissions: [Permission.AiAgentExecute],
  })

  if (!hasSystemAccess) {
    return <AiAgentContext.Provider value={STUB_CONTEXT_VALUE}>{children}</AiAgentContext.Provider>
  }

  return <AiAgentProviderReal>{children}</AiAgentProviderReal>
}

export const AiAgentProviderReal: FunctionComponent<PropsWithChildren<{}>> = ({ children }) => {
  const [registeredFunctions, setRegisteredFunctions] = useState<
    Record<string, RegisteredFunction>
  >({})
  const [registeredData, setRegisteredData] = useState<Record<string, RegisteredData>>({})
  const [registeredPrompts, setRegisteredPrompts] = useState<Record<string, RegisteredPrompt>>({})
  const [maxIterationsOverride, setMaxIterationsOverride] = useState<number | null>(null)
  const [summary, setSummary] = useState<string | null>(null)

  const clearSummary = useCallback(() => {
    setSummary(null)
  }, [])

  // Track which conversation a summary belongs to
  const summaryConversationIdRef = useRef<string | null>(null)
  // Track when we're expecting an AI summary (so we can set the conversation ID later)
  const expectingAISummaryRef = useRef<boolean>(false)

  // Enhanced setSummary with source tracking
  const setSummaryWithTracking = useCallback(
    (newSummary: string | null, fromAI: boolean = false, targetConversationId?: string) => {
      setSummary(newSummary)
      if (fromAI && newSummary) {
        if (targetConversationId) {
          summaryConversationIdRef.current = targetConversationId
        } else {
          // Mark that we're expecting to set the conversation ID later
          expectingAISummaryRef.current = true
        }
      } else if (!fromAI) {
        summaryConversationIdRef.current = null
        expectingAISummaryRef.current = false
      } else if (!newSummary) {
        // Clearing summary
        summaryConversationIdRef.current = null
        expectingAISummaryRef.current = false
      }
    },
    [setSummary]
  )

  // Local enabled state using LocalForage (defaults to true)
  const { data: locallyEnabled = true, setData: setLocallyEnabled } =
    useLocalForageKey<boolean>('aiAgent.enabled')

  const hasAccess = useAccessControl()
  const hasSystemAccess = hasAccess({
    featureToggles: FeatureToggle.IppAiAgent,
    permissions: [Permission.AiAgentExecute],
  })

  // reference count for how many times a page has claimed "it's ready"
  const [pageAiReadyCount, setPageAiReadyCount] = useState(0)
  const isPageAiReady = pageAiReadyCount > 0
  const incrementPageAiReady = useCallback(() => {
    setPageAiReadyCount(prev => prev + 1)
  }, [])
  const decrementPageAiReady = useCallback(() => {
    setPageAiReadyCount(prev => prev - 1)
  }, [])
  const setPageAiReady = useCallback(
    (isReady: boolean) => {
      if (isReady) {
        incrementPageAiReady()
      } else {
        decrementPageAiReady()
      }
    },
    [incrementPageAiReady, decrementPageAiReady]
  )

  // isAgentAvailable considers if the user has access and the page is ready to use the AI agent
  const isAgentAvailable = hasSystemAccess && isPageAiReady
  // isAgentUserEnabled considers if the user has enabled the agent locally
  const isAgentUserEnabled = locallyEnabled
  // isAgentPowered considers if the agent is accessible for the current user, page, and system, and the user has toggled the agent locally to "enabled"
  const isAgentPowered = isAgentAvailable && isAgentUserEnabled

  // Get user metadata for tracking
  const { account } = useCurrentAccountContext()
  const partnerId = usePartnerId({ throwOnMissing: false })
  const retailerId = useRetailerId({ throwOnMissing: false })

  const intlMessages = useDashMessages({
    functionSuccess: 'aiAgent.provider.functionSuccess',
    errorNoData: 'aiAgent.conversation.error.noData',
    errorApiCall: 'aiAgent.conversation.error.apiCall',
    errorMaxIterations: 'aiAgent.conversation.error.maxIterations',
  })

  /**
   * Registers a JavaScript/TypeScript function so that the AI Agent can invoke
   * it through the OpenAI tool-calling workflow.
   *
   * ---------------------------------------------------------------------------
   * Tool-runner contract (what the agent expects)
   * ---------------------------------------------------------------------------
   * 1. Async signature – the runner awaits the registered function because it
   *    must capture the *resolved* value and include it in the next message
   *    sent back to the LLM. Your function therefore must return a Promise
   *    or be declared `async`; otherwise the provider would respond before
   *    the result is available.
   * 2. Single argument – the agent passes a single JSON object whose shape
   *    matches the JSON-Schema supplied in `parameters`.
   * 3. Deterministic result envelope – the function's resolution value is
   *    expected to be JSON-serialisable and, ideally, wrapped in the shape
   *    `{ success: boolean; result: string }` so downstream code can surface
   *    errors or results uniformly.
   *
   * ---------------------------------------------------------------------------
   * Developer-experience helpers (what this wrapper adds for you)
   * ---------------------------------------------------------------------------
   * To save callers from having to conform manually to the above contract we
   * wrap the supplied `fn` with a thin shim that:
   *
   * • Automatically returns a Promise – Sync functions are executed and the
   *   value is awaited/forwarded, so you can export either sync or async code
   *   without thinking about it.
   * • Positional argument convenience – If a JSON-Schema is provided we map
   *   its `properties` order onto positional parameters so you may still author
   *   idiomatic `fn(id, name)` code while the agent uses object arguments.
   * • Uniform return envelope – All outputs are converted to the
   *   `{ success, result }` shape. `void` resolves to a generic success message
   *   and non-string payloads are `JSON.stringify`-ed for you.
   * • Error capture – Thrown exceptions are caught and returned as
   *   `{ success: false, result: <message> }` so the agent sees structured
   *   failures instead of rejected Promises.
   *
   * By normalising every registered function in this way the rest of
   * `AiAgentProvider` can interact with them generically and simply forward the
   * JSON string back to the LLM as the tool response.
   */
  const registerAiFunction = useCallback(
    (registeredFunctionInput: RegisteredFunctionInput) => {
      setRegisteredFunctions(prev => {
        const { fn: rawFn, parameters, ...rest } = registeredFunctionInput

        const wrappedFn: RegisteredFunction['fn'] = async (aiArgumentObject: unknown) => {
          try {
            let result

            if (rawFn.length === 0) {
              result = rawFn()
            } else if (
              parameters?.type === 'object' &&
              parameters?.properties &&
              typeof aiArgumentObject === 'object'
            ) {
              // call the registered function with positional arguments
              const paramKeysFromSchema = Object.keys(parameters.properties)
              const positionalArgs = paramKeysFromSchema.map(
                key => (aiArgumentObject as Record<string, unknown>)[key]
              )

              result = rawFn(...positionalArgs)
            } else {
              // call the registered function with the AI argument object as-is
              result = rawFn(aiArgumentObject)
            }

            if (result instanceof Promise) {
              result = await result
            }

            // if the function itself returns a success object, return it as-is
            if (
              typeof result === 'object' &&
              result !== null &&
              'success' in result &&
              'result' in result
            ) {
              return result as { success: boolean; result: string }
            }

            // For void functions, return generic success object
            if (result === undefined) {
              return { success: true, result: intlMessages.functionSuccess }
            }

            // For functions that return a value, return a success object with the result
            return { success: true, result: JSON.stringify(result) }
          } catch (error: unknown) {
            return { success: false, result: (error as Error).message }
          }
        }

        const newRegisteredFunction: RegisteredFunction = {
          ...rest,
          parameters,
          fn: wrappedFn,
        }

        return { ...prev, [newRegisteredFunction.key]: newRegisteredFunction }
      })
    },
    [intlMessages]
  )

  const getRegisteredFunction = useCallback(
    (key: string) => registeredFunctions[key]?.fn,
    [registeredFunctions]
  )

  const removeAiFunction = useCallback((key: string) => {
    setRegisteredFunctions(prev => {
      const newState = { ...prev }
      delete newState[key]
      return newState
    })
  }, [])

  const registerAiData = useCallback((dataItem: RegisteredData) => {
    setRegisteredData(prev => ({
      ...prev,
      [dataItem.key]: dataItem,
    }))
  }, [])

  const getRegisteredData = useCallback((key: string) => registeredData[key], [registeredData])

  const removeAiData = useCallback((key: string) => {
    setRegisteredData(prev => {
      const newState = { ...prev }
      delete newState[key]
      return newState
    })
  }, [])

  const registerAiPrompt = useCallback((prompt: RegisteredPrompt) => {
    setRegisteredPrompts(prev => ({
      ...prev,
      [prompt.key]: prompt,
    }))
  }, [])

  const getRegisteredPrompt = useCallback(
    (key: string) => registeredPrompts[key],
    [registeredPrompts]
  )

  const removeAiPrompt = useCallback((key: string) => {
    setRegisteredPrompts(prev => {
      const newState = { ...prev }
      delete newState[key]
      return newState
    })
  }, [])

  const registerMaxIterations = useCallback((maxIterations: number) => {
    setMaxIterationsOverride(maxIterations)
  }, [])

  const removeMaxIterations = useCallback(() => {
    setMaxIterationsOverride(null)
  }, [])

  const getRegisteredPrompts = useCallback(
    () => Object.values(registeredPrompts),
    [registeredPrompts]
  )

  const [isChatOpen, setIsChatOpen] = useState(false)
  const openChat = useCallback(() => {
    // Only allow opening chat if agent is powered
    if (!isAgentPowered) {
      return
    }

    // Track chat open
    trackEvent({
      id: 'aiAgent.chatBar.openChat',
      description: 'User opened AI agent chat',
      data: {
        partnerId,
        retailerId,
        canonicalId: account?.canonicalId,
        isInternal: account?.isInternal,
        isAgentPowered,
      },
    })
    setIsChatOpen(true)
  }, [partnerId, retailerId, account?.canonicalId, account?.isInternal, isAgentPowered])
  const closeChat = useCallback(() => setIsChatOpen(false), [])

  // Auto-close chat when agent is not powered (no functions/data available)
  useEffect(() => {
    if (isChatOpen && !isAgentPowered) {
      setIsChatOpen(false)
    }
  }, [isChatOpen, isAgentPowered])

  const [aiAgentExecuteMutation] = useAiAgentExecuteMutation()

  /**
   * Drives a full request-response cycle between the chat UI and mesh AI agent.
   *
   * Overview of the loop:
   * 1. Seed – Begin with the supplied `initialMessages`.
   * 2. Context injection – The provider always ensures there is exactly one
   *    up-to-date `Role.Developer` message at the top of the stack:
   *       • Any data registered via `registerAiData` is serialised and inserted
   *         here so the agent can reference it.
   * 3. Call the agent – The conversation, the current `uiContext`, and a
   *    list of enabled functions (their keys, descriptions, and parameter
   *    schemas taken from `registeredFunctions`) are sent to the backend.
   * 4. Process the agent's reply – Two possibilities:
   *       • `finalResult` is anything other than `'function_call'` → we are
   *         done.  Return the full transcript and the `finalResult` so the UI
   *         can render the assistant's answer.
   *       • `finalResult` is `'function_call'` →
   *            a. Invoke `onProgress` with the assistant message so the UI can
   *               show a "thinking…" state.
   *            b. Parse the `toolCalls` array from that message.
   *            c. Execute each referenced frontend function with the provided
   *               arguments.
   *            d. Wrap each return value in a `Role.Tool` message and push it
   *               onto the thread.
   *            e. Go back to step 2 (new context, new call).
   * 5. Safety net – Iterations are limited by `MAX_ITERATIONS` to avoid
   *    infinite loops if the agent keeps requesting tools indefinitely.
   *
   * @param initialMessages Conversation seed.
   * @param uiContext       Snapshot of relevant UI state.
   * @param onProgress      Callback fired when the agent requests a tool.
   * @returns               Final conversation and reason for termination.
   */
  const executeAgentRequest: AiAgentContextValue['executeAgentRequest'] = useCallback(
    async (initialMessages, uiContext, onProgress) => {
      // Remove any developer-context messages that may have been carried over
      // from a previous iteration so we can inject a fresh, up-to-date one in
      // the next step.
      let messagesThread: MessageInput[] = initialMessages.filter(
        msg => msg.role !== Role.Developer
      )

      // Use registered max iterations override, or fall back to default
      const MAX_ITERATIONS = maxIterationsOverride ?? 10

      for (let i = 0; i < MAX_ITERATIONS; i++) {
        let messagesForApiCall: MessageInput[]

        // Inject a single developer message with any host-provided context so
        // the agent can see application state that isn't part of the chat
        // transcript.
        if (Object.keys(registeredData).length > 0) {
          const developerContextMessage: MessageInput = {
            role: Role.Developer,
            content: JSON.stringify(
              Object.fromEntries(
                Object.values(registeredData).map((rd: RegisteredData) => [
                  rd.key,
                  { description: rd.description, data: rd.data },
                ])
              ),
              null,
              2
            ),
          }
          // Prepend developer context message
          messagesForApiCall = [developerContextMessage, ...messagesThread]
        } else {
          messagesForApiCall = [...messagesThread]
        }

        // Compose the payload for the backend: conversation so far, UI context
        // and the list of functions the agent is allowed to call.
        const agentRequestInput: AgentRequestInput = {
          messages: messagesForApiCall,
          uiContext,
          tools: Object.values(registeredFunctions).map(rf => ({
            type: 'function',
            function: {
              name: rf.key,
              description: rf.description,
              parameters: rf.parameters,
            },
          })),
        }

        let agentResponse: AgentResponse // Populated below after mutation
        try {
          // Fire the GraphQL mutation that forwards everything to the mesh AI
          // service.
          const mutationResponse = await aiAgentExecuteMutation({
            variables: { input: agentRequestInput },
          })

          if (!mutationResponse.data?.aiAgentExecute) {
            console.error('AI agent execution failed or returned no data from mutation')
            return {
              messages: [
                ...(messagesThread as Message[]),
                {
                  role: Role.Assistant,
                  content: intlMessages.errorNoData,
                  toolCalls: [],
                  toolCallId: '',
                },
              ],
              finalResult: 'ERROR_NO_DATA',
              summary: summary, // Keep existing summary
            }
          }

          agentResponse = mutationResponse.data.aiAgentExecute
        } catch (error) {
          // Network or server error – surface the problem as an assistant
          // message so the UI can display it.
          console.error('Error executing AI agent request via mutation:', error)
          return {
            messages: [
              ...(messagesThread as Message[]),
              {
                role: Role.Assistant,
                content: intlMessages.errorApiCall.replace('{error}', (error as Error).message),
                toolCalls: [],
                toolCallId: '',
              },
            ],
            finalResult: 'ERROR_MUTATION_FAILED',
            summary: summary, // Keep existing summary
          }
        }

        // Agent returns entire conversation history, so process messages for this step
        const messagesFromAgent = agentResponse.messages.map(msg => ({
          role: msg.role,
          content: msg.content,
          toolCalls:
            msg.role === Role.Assistant && msg.toolCalls
              ? msg.toolCalls.map(tc => ({
                  id: tc.id,
                  type: 'function',
                  function: {
                    name: tc.function.name,
                    arguments: tc.function.arguments,
                  },
                }))
              : undefined,
          toolCallId: msg.toolCallId,
        }))

        // The agent either answered normally or there was some anomaly – in
        // both cases we exit the loop and give the UI the full transcript.
        if (agentResponse.finalResult !== 'function_call' || !messagesFromAgent.length) {
          // Store the summary from the agent response
          setSummaryWithTracking(agentResponse.summary || null, true)
          return {
            messages: messagesFromAgent,
            finalResult: agentResponse.finalResult,
            summary: agentResponse.summary,
          }
        }

        // Store the summary from the agent response for function calls too
        setSummaryWithTracking(agentResponse.summary || null, true)

        // If it's a function call, prepare messagesThread for the *next* iteration.
        // We take the agent's messages from this step, filter out any Developer role messages
        // (as a fresh one will be added), and this becomes the base for the next call.
        messagesThread = messagesFromAgent.filter(msg => msg.role !== Role.Developer)

        const lastAssistantMessage = messagesFromAgent.findLast(
          msg => msg.role === Role.Assistant && msg.toolCalls && msg.toolCalls.length > 0
        )

        if (!lastAssistantMessage || !lastAssistantMessage.toolCalls) {
          console.warn(
            "finalResult is 'function_call', but no valid toolCalls found in the last assistant message."
          )
          return {
            messages: messagesFromAgent,
            finalResult: 'ERROR_NO_TOOL_CALLS',
            summary: summary, // Keep existing summary
          }
        }

        // Inform the UI that the agent has requested a function so we can show feedback.
        if (onProgress) {
          onProgress(lastAssistantMessage)
        }

        const toolResultMessages: MessageInput[] = []

        for (const toolCall of lastAssistantMessage.toolCalls) {
          let toolCallResult: string

          const functionName = toolCall.function.name
          const functionToExecute = getRegisteredFunction(functionName)

          // May be undefined if not registered
          if (functionToExecute) {
            try {
              const args = JSON.parse(toolCall.function.arguments || '{}')
              const result = await functionToExecute(args)
              toolCallResult = JSON.stringify(result)
            } catch (e) {
              toolCallResult = JSON.stringify({
                error: `Execution error in ${functionName}: ${
                  e instanceof Error ? e.message : String(e)
                }`,
              })
            }
          } else {
            toolCallResult = JSON.stringify({ error: `Function ${functionName} not found.` })
          }

          toolResultMessages.push({
            toolCallId: toolCall.id,
            role: Role.Tool,
            content: toolCallResult,
          })
        }

        // Add tool responses to the history for the next API call
        messagesThread.push(...toolResultMessages)
      }

      console.error('Max tool call iterations reached.')
      messagesThread.push({
        role: Role.Assistant,
        content: intlMessages.errorMaxIterations,
        toolCalls: [],
        toolCallId: '',
      })
      return {
        messages: messagesThread as Message[],
        finalResult: 'ERROR_MAX_ITERATIONS',
        summary: summary, // Keep existing summary
      }
    },
    [
      aiAgentExecuteMutation,
      getRegisteredFunction,
      registeredFunctions,
      registeredData,
      intlMessages,
      summary,
      setSummaryWithTracking,
      maxIterationsOverride,
    ]
  )

  // Conversation history management
  const {
    conversations,
    saveConversation,
    loadConversation: loadConversationFromHistory,
    deleteConversation,
    clearAllConversations,
  } = useConversationHistory()

  const {
    messages,
    sendMessage,
    clearConversation: originalClearConversation,
    loadConversationMessages,
    updateMessage,
    updateMessageFeedback,
    updateMessageComment,
    loading,
    conversationId,
  } = useAiAgentConversation(executeAgentRequest, clearSummary)

  // Set conversation ID for AI-generated summaries that are waiting for it
  React.useEffect(() => {
    if (expectingAISummaryRef.current && conversationId && summary) {
      summaryConversationIdRef.current = conversationId
      expectingAISummaryRef.current = false
    }
  }, [conversationId, summary])

  // Auto-save conversation when summary is updated (conversation is complete)
  React.useEffect(() => {
    if (summary && messages.length > 0 && conversationId) {
      // Only auto-save if the summary belongs to the current conversation
      if (summaryConversationIdRef.current === conversationId) {
        saveConversation(conversationId, messages, summary)
      }
    }
  }, [summary, messages, conversationId, saveConversation])

  // Enhanced clear conversation that saves current conversation to history
  const clearConversation = useCallback(() => {
    if (messages.length > 0 && conversationId) {
      // Save current conversation to history before clearing
      saveConversation(conversationId, messages, summary)
    }
    originalClearConversation()
    // Clear the summary from UI state for new conversation
    setSummaryWithTracking(null, false)
  }, [
    messages,
    conversationId,
    summary,
    saveConversation,
    originalClearConversation,
    setSummaryWithTracking,
  ])

  // Clear conversation without saving to history (for clear all scenarios)
  const clearConversationWithoutSaving = useCallback(() => {
    originalClearConversation()
    // Clear the summary from UI state for new conversation
    setSummaryWithTracking(null, false)
  }, [originalClearConversation, setSummaryWithTracking])

  // Load a conversation from history
  const loadConversation = useCallback(
    (historyConversationId: string) => {
      const conversation = loadConversationFromHistory(historyConversationId)
      if (conversation) {
        // First save current conversation if it has messages
        if (messages.length > 0 && conversationId && conversationId !== historyConversationId) {
          saveConversation(conversationId, messages, summary)
        }

        // Load the historical conversation
        loadConversationMessages(conversation.id, conversation.messages)
        // Set the summary from the loaded conversation (this won't trigger auto-save)
        setSummaryWithTracking(conversation.summary, false)
      }
    },
    [
      loadConversationFromHistory,
      messages,
      conversationId,
      summary,
      saveConversation,
      loadConversationMessages,
      setSummaryWithTracking,
    ]
  )

  const contextValue = useMemo<AiAgentContextValue>(
    () => ({
      registerAiFunction: registerAiFunction as AiAgentContextValue['registerAiFunction'],
      getRegisteredFunction,
      removeAiFunction,
      registerAiData,
      getRegisteredData,
      removeAiData,
      registerAiPrompt,
      getRegisteredPrompt,
      removeAiPrompt,
      getRegisteredPrompts,
      registerMaxIterations,
      removeMaxIterations,
      executeAgentRequest,
      isAgentAvailable,
      isAgentUserEnabled,
      isChatOpen,
      openChat,
      closeChat,
      messages,
      sendMessage,
      clearConversation,
      clearConversationWithoutSaving,
      updateMessage,
      updateMessageFeedback,
      updateMessageComment,
      loading,
      isAgentPowered,
      isPageAiReady,
      setPageAiReady,
      setEnabled: setLocallyEnabled,
      summary,
      conversationHistory: conversations,
      currentConversationId: conversationId,
      loadConversation,
      deleteConversation,
      clearAllConversations,
    }),
    [
      registerAiFunction,
      removeAiFunction,
      registerAiData,
      removeAiData,
      registerAiPrompt,
      removeAiPrompt,
      getRegisteredPrompt,
      getRegisteredPrompts,
      registerMaxIterations,
      removeMaxIterations,
      executeAgentRequest,
      getRegisteredFunction,
      getRegisteredData,
      isAgentAvailable,
      isAgentUserEnabled,
      isChatOpen,
      openChat,
      closeChat,
      messages,
      sendMessage,
      clearConversation,
      clearConversationWithoutSaving,
      updateMessage,
      updateMessageFeedback,
      updateMessageComment,
      loading,
      isAgentPowered,
      isPageAiReady,
      setPageAiReady,
      setLocallyEnabled,
      summary,
      conversations,
      conversationId,
      loadConversation,
      deleteConversation,
      clearAllConversations,
    ]
  )

  return (
    <CommentModalProvider>
      <AiAgentContext.Provider value={contextValue}>{children}</AiAgentContext.Provider>
    </CommentModalProvider>
  )
}
